import { getCurrentLanguage } from "@abp";
import { BellOutlined, UserOutlined } from "@ant-design/icons";
import {
  useFirebaseNotification,
  useFirebaseToken,
  useLatestAnnouncements,
  usePWAInstall,
  useSignalR,
} from "@hooks";
import { useStore } from "@stores";
import { Badge, Empty, Image, Popover, Skeleton } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/sr";
import relativeTime from "dayjs/plugin/relativeTime";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { PwaBanner } from "./PwaBanner";
import Styles from "./TopHeaderMenu.styles";
import { UserDrawer } from "./UserDrawer";

dayjs.extend(relativeTime);

/**
 * TOP HEADER MENU — Sticky frosted-glass header.
 *
 * Layout: [Logo] ··· [Bell] [Auth buttons OR Avatar (opens drawer)]
 *
 * Logged-in users get a rich drawer (profile header, upcoming booking,
 * shortcuts, account, support, settings) opened via the avatar button.
 */
export const TopHeaderMenu: React.FC = observer(() => {
  const { t } = useTranslation();
  const { authStore } = useStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);

  useSignalR();
  useFirebaseToken();
  useFirebaseNotification();
  const { showBanner, showIOSBanner, promptInstall, dismissBanner } =
    usePWAInstall();
  const {
    latest,
    count,
    loading: announcementsLoading,
  } = useLatestAnnouncements();

  const isSr = getCurrentLanguage().startsWith("sr");

  const bellContent = (
    <Styles.BellDropdown>
      <Styles.BellHeader>
        <Styles.BellTitle>{t("Announcements")}</Styles.BellTitle>
        {latest.length > 0 && (
          <Styles.BellSeeAll
            onClick={() => {
              navigate("/announcements");
              setBellOpen(false);
            }}
          >
            {t("SeeAll")}
          </Styles.BellSeeAll>
        )}
      </Styles.BellHeader>
      <Styles.BellList>
        {announcementsLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : latest.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("NoAnnouncements")}
          />
        ) : (
          latest.map((a) => (
            <Styles.BellItem key={a.id}>
              <Styles.BellItemContent>
                {a.tenantLogo ? (
                  <Styles.BellItemLogo
                    src={a.tenantLogo}
                    alt={a.tenantName}
                    width={36}
                    height={36}
                    loading="lazy"
                  />
                ) : (
                  <Styles.BellItemLogoFallback>
                    {a.tenantName?.[0]?.toUpperCase()}
                  </Styles.BellItemLogoFallback>
                )}
                <Styles.BellItemText>
                  <Styles.BellItemTitle>{a.title}</Styles.BellItemTitle>
                  <Styles.BellItemTenant>{a.tenantName}</Styles.BellItemTenant>
                </Styles.BellItemText>
                <Styles.BellItemTime>
                  {dayjs(a.creationTime)
                    .locale(isSr ? "sr" : "en")
                    .fromNow()}
                </Styles.BellItemTime>
              </Styles.BellItemContent>
            </Styles.BellItem>
          ))
        )}
      </Styles.BellList>
    </Styles.BellDropdown>
  );

  const bellIcon = (
    <Popover
      content={bellContent}
      trigger="click"
      open={bellOpen}
      onOpenChange={setBellOpen}
      placement="bottomRight"
      arrow={false}
      overlayClassName="bell-popover"
    >
      <Styles.BellBtn>
        <Badge count={count} size="small" offset={[-2, 2]}>
          <BellOutlined style={{ fontSize: 20 }} />
        </Badge>
      </Styles.BellBtn>
    </Popover>
  );

  const isAuthPage = pathname === "/login" || pathname === "/registration";

  return (
    <>
      <Styles.StyledHeader>
        <Styles.HeaderRow>
          <Styles.LogoCol onClick={() => navigate("/")}>
            <Styles.LogoIcon>
              <Image
                width={36}
                height={36}
                preview={false}
                src="/favicon-96.png"
                alt="Rasporedi Me"
                style={{ borderRadius: "22%" }}
              />
            </Styles.LogoIcon>
            <Styles.BrandText>Rasporedi Me</Styles.BrandText>
          </Styles.LogoCol>

          {/* Actions */}
          <Styles.ActionsCol>
            {!isAuthPage && (
              <>
                {bellIcon}

                {!authStore.currentUserId ? (
                  <>
                    <Styles.LoginBtn
                      type="text"
                      onClick={() => navigate("/login")}
                    >
                      {t("LogInHeader")}
                    </Styles.LoginBtn>
                    <Styles.RegisterBtn
                      type="primary"
                      onClick={() => navigate("/registration")}
                    >
                      {t("RegisterMeHeader")}
                    </Styles.RegisterBtn>
                  </>
                ) : (
                  <>
                    <Styles.DesktopOnly>
                      <Styles.UserAvatar
                        size={40}
                        icon={<UserOutlined />}
                        onClick={() => setDrawerVisible(true)}
                      />
                    </Styles.DesktopOnly>
                    <Styles.MobileOnly>
                      <Styles.UserAvatar
                        size={36}
                        icon={<UserOutlined />}
                        onClick={() => setDrawerVisible(true)}
                      />
                    </Styles.MobileOnly>
                  </>
                )}
              </>
            )}
          </Styles.ActionsCol>
        </Styles.HeaderRow>

        <UserDrawer
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
        />
      </Styles.StyledHeader>

      {/* PWA Install Banner — slides in below header, branded to match design system */}
      {showBanner && (
        <PwaBanner
          onInstall={promptInstall}
          onDismiss={dismissBanner}
          isIOS={showIOSBanner}
        />
      )}
    </>
  );
});
